import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle, ShieldCheck, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { intakeFormSchema, IntakeFormData } from "./IntakeFormSchema";
import { PersonalInfoSection } from "./PersonalInfoSection";
import { InsuranceInfoSection } from "./InsuranceInfoSection";
import { MedicalInfoSection } from "./MedicalInfoSection";
import { AccessibilitySection } from "./AccessibilitySection";
import { SocialSection } from "./SocialSection";
import { SdohSection } from "./SdohSection";
import { PhotoUploadSection } from "./PhotoUploadSection";
import { ConsentSection } from "./ConsentSection";
import { VerificationModal } from "./VerificationModal";

interface IntakeFormProps {
  /** When true, skips identity verification and uses a generated user_id */
  providerMode?: boolean;
  /** Where to redirect after successful submission */
  redirectTo?: string;
  /** Pre-fill form with existing data for editing */
  editData?: Partial<IntakeFormData>;
  /** Existing patient record ID — triggers UPDATE instead of INSERT */
  patientId?: string;
}

const emptyDefaults: IntakeFormData = {
  first_name: "",
  last_name: "",
  date_of_birth: "",
  ssn_last_four: "",
  phone: "",
  email: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  zip_code: "",
  insurance_type: undefined as any,
  medicare_id: "",
  medicaid_id: "",
  medicare_part_a: false,
  medicare_part_b: false,
  medicare_part_c: false,
  medicare_part_d: false,
  primary_care_physician: "",
  pcp_phone: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  emergency_contact_relationship: "",
  current_medications: "",
  allergies: "",
  chronic_conditions: "",
  mobility_aids: [],
  hearing_devices: [],
  vision_devices: [],
  communication_devices: [],
  other_assistive_devices: "",
  accessibility_needs: "",
  hobbies: [],
  social_activities: [],
  living_situation: "",
  social_support_level: "",
  preferred_communication_style: "",
  interests_notes: "",
  sdoh_screenings: [],
  sdoh_notes: "",
  patient_photo_url: "",
  insurance_card_front_url: "",
  insurance_card_back_url: "",
  consent_given: false,
  signature: "",
};

export function IntakeForm({ providerMode = false, redirectTo, editData, patientId }: IntakeFormProps) {
  const isEditMode = !!patientId;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [pendingData, setPendingData] = useState<IntakeFormData | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const form = useForm<IntakeFormData>({
    resolver: zodResolver(intakeFormSchema),
    defaultValues: editData ? { ...emptyDefaults, ...editData } : emptyDefaults,
  });

  const handleFormSubmit = async (data: IntakeFormData) => {
    if (!user) {
      toast.error("You must be logged in to submit this form");
      return;
    }

    if (isEditMode) {
      // Edit mode — skip verification, update directly
      submitForm(data);
    } else if (providerMode) {
      if (!data.email) {
        toast.error("Email is required when registering a new member");
        return;
      }
      submitForm(data);
    } else {
      setPendingData(data);
      setShowVerification(true);
    }
  };

  const submitForm = async (data: IntakeFormData) => {
    if (!user) {
      toast.error("You must be logged in to submit this form");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        // UPDATE existing patient record
        const { error } = await supabase.from("patients").update({
          first_name: data.first_name,
          last_name: data.last_name,
          date_of_birth: data.date_of_birth,
          ssn_last_four: data.ssn_last_four || null,
          phone: data.phone,
          email: data.email || null,
          address_line1: data.address_line1,
          address_line2: data.address_line2 || null,
          city: data.city,
          state: data.state,
          zip_code: data.zip_code,
          insurance_type: data.insurance_type,
          medicare_id: data.medicare_id || null,
          medicaid_id: data.medicaid_id || null,
          medicare_part_a: data.medicare_part_a,
          medicare_part_b: data.medicare_part_b,
          medicare_part_c: data.medicare_part_c,
          medicare_part_d: data.medicare_part_d,
          primary_care_physician: data.primary_care_physician || null,
          pcp_phone: data.pcp_phone || null,
          emergency_contact_name: data.emergency_contact_name,
          emergency_contact_phone: data.emergency_contact_phone,
          emergency_contact_relationship: data.emergency_contact_relationship,
          current_medications: data.current_medications || null,
          allergies: data.allergies || null,
          chronic_conditions: data.chronic_conditions || null,
          mobility_aids: data.mobility_aids || [],
          hearing_devices: data.hearing_devices || [],
          vision_devices: data.vision_devices || [],
          communication_devices: data.communication_devices || [],
          other_assistive_devices: data.other_assistive_devices || null,
          accessibility_needs: data.accessibility_needs || null,
          hobbies: data.hobbies || [],
          social_activities: data.social_activities || [],
          living_situation: data.living_situation || null,
          social_support_level: data.social_support_level || null,
          preferred_communication_style: data.preferred_communication_style || null,
          interests_notes: data.interests_notes || null,
          sdoh_screenings: data.sdoh_screenings || [],
          sdoh_notes: data.sdoh_notes || null,
          patient_photo_url: data.patient_photo_url || null,
          insurance_card_front_url: data.insurance_card_front_url || null,
          insurance_card_back_url: data.insurance_card_back_url || null,
          consent_given: data.consent_given,
          signature: data.signature,
        }).eq("id", patientId);

        if (error) throw error;

        // Sync names to profiles
        await supabase.from("profiles").update({
          first_name: data.first_name,
          last_name: data.last_name,
          display_name: `${data.first_name} ${data.last_name}`.trim(),
        } as any).eq("id", user.id);

        // Sentry logging for edit
        Sentry.addBreadcrumb({ category: "intake", message: "Patient data updated", level: "info" });
        Sentry.captureMessage(`User ${user.id} updated intake form on ${new Date().toISOString()}`, {
          level: "info",
          extra: { patientId, userId: user.id },
        });

        setIsSubmitted(true);
        toast.success("Your information has been updated successfully!");
      } else {
        // INSERT — original logic
        let patientUserId = user.id;

        if (providerMode) {
          const { data: profileData } = await supabase
            .from("profiles")
            .select("display_name")
            .eq("id", user.id)
            .maybeSingle();

          const providerName = profileData?.display_name || "Your provider";

          const { data: accountData, error: accountError } = await supabase.functions.invoke(
            "create-member-account",
            {
              body: {
                email: data.email,
                first_name: data.first_name,
                last_name: data.last_name,
                provider_name: providerName,
              },
            }
          );

          if (accountError) {
            throw new Error(accountError.message || "Failed to create member account");
          }

          if (!accountData?.user_id) {
            throw new Error("No user ID returned from account creation");
          }

          patientUserId = accountData.user_id;

          if (accountData.is_new) {
            toast.info("Member account created — a welcome email has been sent to " + data.email);
          }
        }

        const { error } = await supabase.from("patients").insert({
          user_id: patientUserId,
          first_name: data.first_name,
          last_name: data.last_name,
          date_of_birth: data.date_of_birth,
          ssn_last_four: data.ssn_last_four || null,
          phone: data.phone,
          email: data.email || null,
          address_line1: data.address_line1,
          address_line2: data.address_line2 || null,
          city: data.city,
          state: data.state,
          zip_code: data.zip_code,
          insurance_type: data.insurance_type,
          medicare_id: data.medicare_id || null,
          medicaid_id: data.medicaid_id || null,
          medicare_part_a: data.medicare_part_a,
          medicare_part_b: data.medicare_part_b,
          medicare_part_c: data.medicare_part_c,
          medicare_part_d: data.medicare_part_d,
          primary_care_physician: data.primary_care_physician || null,
          pcp_phone: data.pcp_phone || null,
          emergency_contact_name: data.emergency_contact_name,
          emergency_contact_phone: data.emergency_contact_phone,
          emergency_contact_relationship: data.emergency_contact_relationship,
          current_medications: data.current_medications || null,
          allergies: data.allergies || null,
          chronic_conditions: data.chronic_conditions || null,
          mobility_aids: data.mobility_aids || [],
          hearing_devices: data.hearing_devices || [],
          vision_devices: data.vision_devices || [],
          communication_devices: data.communication_devices || [],
          other_assistive_devices: data.other_assistive_devices || null,
          accessibility_needs: data.accessibility_needs || null,
          hobbies: data.hobbies || [],
          social_activities: data.social_activities || [],
          living_situation: data.living_situation || null,
          social_support_level: data.social_support_level || null,
          preferred_communication_style: data.preferred_communication_style || null,
          interests_notes: data.interests_notes || null,
          sdoh_screenings: data.sdoh_screenings || [],
          sdoh_notes: data.sdoh_notes || null,
          patient_photo_url: data.patient_photo_url || null,
          insurance_card_front_url: data.insurance_card_front_url || null,
          insurance_card_back_url: data.insurance_card_back_url || null,
          consent_given: data.consent_given,
          consent_date: new Date().toISOString(),
          signature: data.signature,
          status: "pending",
        });

        if (error) throw error;

        // Sync structured names to profiles for the member
        await supabase.from("profiles").update({
          first_name: data.first_name,
          last_name: data.last_name,
          display_name: `${data.first_name} ${data.last_name}`.trim(),
        } as any).eq("id", patientUserId);

        // Auto-assign provider to this member in provider mode
        if (providerMode && user) {
          await supabase.from("provider_members" as any).upsert(
            { provider_id: user.id, member_id: patientUserId },
            { onConflict: "provider_id,member_id" }
          );
        }

        // Sentry logging for new submission
        Sentry.addBreadcrumb({ category: "intake", message: "New patient intake submitted", level: "info" });
        Sentry.captureMessage(`User ${user.id} submitted new intake form on ${new Date().toISOString()}`, {
          level: "info",
          extra: { userId: user.id },
        });

        setIsSubmitted(true);
        toast.success("Intake form submitted successfully!");
      }
    } catch (error: any) {
      console.error("Error submitting intake form:", error);
      toast.error(error.message || "Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerificationComplete = () => {
    setShowVerification(false);
    if (pendingData) {
      submitForm(pendingData);
    }
  };

  const handleVerificationClose = () => {
    setShowVerification(false);
    setPendingData(null);
  };

  if (isSubmitted) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-primary mx-auto" />
            <h2 className="text-2xl font-bold text-foreground">
              {isEditMode ? "Changes Saved Successfully!" : "Form Submitted Successfully!"}
            </h2>
            <p className="text-muted-foreground">
              {isEditMode
                ? "Your information has been updated. You can view your changes on your profile."
                : "Thank you for completing your intake form. Our team will review your information and contact you shortly."}
            </p>
            <Button onClick={() => navigate(redirectTo || (isEditMode ? ROUTES.MEMBER_PROFILE : ROUTES.MEMBER))} className="mt-4">
              {isEditMode ? "Back to Profile" : "Return to Dashboard"}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get email and phone for verification from form values or user
  const formEmail = form.watch("email");
  const formPhone = form.watch("phone");
  const verificationEmail = formEmail || user?.email || null;
  const verificationPhone = formPhone || null;

  return (
    <>
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl">
            {isEditMode ? "Edit Your Information" : "Patient Intake Form"}
          </CardTitle>
          <CardDescription>
            {isEditMode
              ? "Update your personal, insurance, and medical information below."
              : "Medicare/Medicaid New Patient Evaluation – Please complete all required fields (*)"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-8">
              <PersonalInfoSection form={form} />
              <InsuranceInfoSection form={form} />
              <MedicalInfoSection form={form} />
              <AccessibilitySection form={form} />
              <SocialSection form={form} />
              <SdohSection form={form} />
              <PhotoUploadSection form={form} />
              <ConsentSection form={form} />

              <div className="flex justify-end gap-4 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => isEditMode ? navigate(redirectTo || ROUTES.MEMBER_PROFILE) : form.reset()}
                  disabled={isSubmitting}
                >
                  {isEditMode ? "Cancel" : "Reset Form"}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : isEditMode ? (
                    <Save className="mr-2 h-4 w-4" />
                  ) : (
                    <ShieldCheck className="mr-2 h-4 w-4" />
                  )}
                  {isEditMode ? "Save Changes" : "Verify & Submit"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {!isEditMode && (
        <VerificationModal
          open={showVerification}
          onClose={handleVerificationClose}
          onVerified={handleVerificationComplete}
          userEmail={verificationEmail}
          userPhone={verificationPhone}
        />
      )}
    </>
  );
}
